"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Link from "next/link";
import type { FUSportsLivescoreCompetitionGroup, FUSportsLivescoreEvent, SearchEntityResult } from "fansunited-data-layer";
import { cn } from "@/utilities/core/cn";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { BettingOdds } from "fansunited-sports-ui";
import { useHomeLivescoreData } from "./HomeLivescoreProvider";
import type { MatchOddsMap, OddsData } from "@/features/odds/types";
import { applyGroupOrdering } from "@/views/livescore/utils/grouping";
import { asCompetition, asCompetitor, formatFootballLiveMinute, getResultScore } from "@/views/livescore/utils/livescore";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { formatTimeHHMM } from "@/utilities/formatting/date";
import { useBetslip } from "@/features/betslip/useBetslip";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SportBadge } from "./SportBadge";
interface TickerSettings {
  label?: string | null;
  cta?: string | null;
  ctaLink?: string | null;
}
interface PopularMatchesTickerProps {
  popularCompetitionIds: string[];
  ticker?: TickerSettings;
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function SkeletonPill(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("h-6 rounded-full bg-muted/50 animate-pulse", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function SkeletonCard() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SkeletonPill className="w-12 h-3 rounded" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded-full bg-muted/50 animate-pulse shrink-0" /><SkeletonPill className="flex-1 h-3 rounded" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="space-y-1.5">{t1}<div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded-full bg-muted/50 animate-pulse shrink-0" /><SkeletonPill className="flex-1 h-3 rounded" /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="bg-card rounded-md p-2 w-52 shrink-0 border border-border/50 space-y-2">{t0}{t2}<div className="flex gap-0.5"><SkeletonPill className="flex-1 h-6 rounded-sm" /><SkeletonPill className="flex-1 h-6 rounded-sm" /><SkeletonPill className="flex-1 h-6 rounded-sm" /></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function TickerSkeleton(t0) {
  const $ = _c(4);
  const {
    ticker
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border/50"><SkeletonPill className="w-14" /><SkeletonPill className="w-10" /><SkeletonPill className="w-28" /><SkeletonPill className="w-20" /><SkeletonPill className="w-24" /><SkeletonPill className="w-16" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex gap-1.5 px-3 py-2.5 overflow-hidden">{Array.from({
        length: 7
      }).map(_temp)}</div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== ticker) {
    t3 = <div className="bg-card rounded-lg overflow-hidden border border-border">{t1}{t2}<BottomBar ticker={ticker} /></div>;
    $[2] = ticker;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function _temp(_, i) {
  return <SkeletonCard key={i} />;
}
function LiveDot() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="inline-block w-1.5 h-1.5 rounded-full bg-error shrink-0 animate-pulse-subtle" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function OddsSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex gap-0.5 px-2 pb-2 pt-2"><div className="flex-1 h-6 rounded-sm bg-muted/50 animate-pulse" /><div className="flex-1 h-6 rounded-sm bg-muted/50 animate-pulse" /><div className="flex-1 h-6 rounded-sm bg-muted/50 animate-pulse" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
interface MatchCardProps {
  event: FUSportsLivescoreEvent;
  entities: Record<string, SearchEntityResult>;
  sport: string;
  oddsMap: MatchOddsMap;
  oddsLoading: boolean;
  isFootball: boolean;
}
function MatchCard(t0) {
  const $ = _c(85);
  const {
    event,
    entities,
    sport,
    oddsMap,
    oddsLoading,
    isFootball
  } = t0;
  const nav = useNavigation();
  const {
    addOddsSelection,
    canAddOdds
  } = useBetslip();
  const tenantSettings = useTenantSettings();
  const labels = useLabels();
  let t1;
  if ($[0] !== event.status?.type) {
    t1 = event.status?.type?.toUpperCase();
    $[0] = event.status?.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const statusType = t1;
  const isLive = statusType === "LIVE";
  const isFinished = statusType === "FINISHED";
  const showScore = isLive || isFinished;
  let t2;
  if ($[2] !== event.status || $[3] !== event.timing || $[4] !== isFootball || $[5] !== labels) {
    t2 = isFootball ? formatFootballLiveMinute(event.status, event.timing?.currentMinute, labels.livescore) : event.timing?.currentMinute !== undefined ? `${event.timing.currentMinute}'` : null;
    $[2] = event.status;
    $[3] = event.timing;
    $[4] = isFootball;
    $[5] = labels;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const liveMinuteDisplay = t2;
  let t3;
  if ($[7] !== entities || $[8] !== event.competitorOne.id) {
    t3 = asCompetitor(entities, event.competitorOne.id);
    $[7] = entities;
    $[8] = event.competitorOne.id;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const c1 = t3;
  let t4;
  if ($[10] !== entities || $[11] !== event.competitorTwo.id) {
    t4 = asCompetitor(entities, event.competitorTwo.id);
    $[10] = entities;
    $[11] = event.competitorTwo.id;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const c2 = t4;
  const nameOne = c1?.name ?? event.competitorOne.id;
  const nameTwo = c2?.name ?? event.competitorTwo.id;
  let t5;
  if ($[13] !== event.score) {
    t5 = getResultScore(event.score);
    $[13] = event.score;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const total = t5;
  const s1 = total.competitorOne;
  const s2 = total.competitorTwo;
  const numS1 = Number(s1);
  const numS2 = Number(s2);
  const c1Won = isFinished && !isNaN(numS1) && !isNaN(numS2) && numS1 > numS2;
  const c2Won = isFinished && !isNaN(numS1) && !isNaN(numS2) && numS2 > numS1;
  let t6;
  if ($[15] !== event.startTime) {
    const kickoff = event.startTime ? new Date(event.startTime) : null;
    t6 = kickoff ? formatTimeHHMM(kickoff) : null;
    $[15] = event.startTime;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const timeStr = t6;
  let t7;
  if ($[17] !== event.id || $[18] !== oddsMap) {
    t7 = oddsMap.get(event.id)?.[0];
    $[17] = event.id;
    $[18] = oddsMap;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const bookmaker = t7;
  let t8;
  if ($[20] !== event.id || $[21] !== isFootball || $[22] !== nav) {
    t8 = isFootball ? nav.match(event.id) : undefined;
    $[20] = event.id;
    $[21] = isFootball;
    $[22] = nav;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const matchHref = t8;
  const shouldInterceptOdds = canAddOdds && isFootball;
  let t9;
  if ($[24] !== addOddsSelection || $[25] !== event.id || $[26] !== event.startTime || $[27] !== shouldInterceptOdds) {
    t9 = odd => {
      if (!shouldInterceptOdds) {
        return;
      }
      addOddsSelection(event.id, "FT_1X2", odd.label, event.startTime ?? undefined);
    };
    $[24] = addOddsSelection;
    $[25] = event.id;
    $[26] = event.startTime;
    $[27] = shouldInterceptOdds;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const handleOddsClick = t9;
  let t10;
  bb0: {
    if (!bookmaker || !shouldInterceptOdds) {
      t10 = null;
      break bb0;
    }
    let t11;
    if ($[29] !== bookmaker.odds || $[30] !== handleOddsClick) {
      let t12;
      if ($[32] !== handleOddsClick) {
        t12 = t13 => {
          const {
            change,
            label,
            ...rest
          } = t13;
          return {
            ...rest,
            label,
            onClick: () => handleOddsClick({
              label,
              ...rest
            }),
            url: undefined
          };
        };
        $[32] = handleOddsClick;
        $[33] = t12;
      } else {
        t12 = $[33];
      }
      t11 = bookmaker.odds.map(t12);
      $[29] = bookmaker.odds;
      $[30] = handleOddsClick;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t10 = t11;
  }
  const processedOdds = t10;
  const hasBookmakerOdds = bookmaker && bookmaker.odds.length > 0;
  const showOdds = tenantSettings.hasOdds && hasBookmakerOdds;
  let t11;
  if ($[34] !== sport) {
    t11 = sport.toLowerCase();
    $[34] = sport;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11) {
    t12 = <SportBadge sport={t11} className="text-[11px] opacity-70" />;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== isFinished || $[39] !== isLive || $[40] !== liveMinuteDisplay || $[41] !== timeStr) {
    t13 = isLive ? <span className="text-error text-[10px] font-bold flex items-center gap-1"><LiveDot />{liveMinuteDisplay ?? "LIVE"}</span> : isFinished ? <span className="text-muted-foreground/60 text-[10px] font-semibold">FT</span> : <span className="text-muted-foreground text-[10px] font-semibold tabular-nums">{timeStr}</span>;
    $[38] = isFinished;
    $[39] = isLive;
    $[40] = liveMinuteDisplay;
    $[41] = timeStr;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t12 || $[44] !== t13) {
    t14 = <div className="flex items-center gap-1.5 mb-1.5">{t12}{t13}</div>;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== c1 || $[47] !== c1Won || $[48] !== c2Won || $[49] !== event.competitorOne.id || $[50] !== nameOne || $[51] !== s1) {
    t15 = {
      competitor: c1,
      id: event.competitorOne.id,
      name: nameOne,
      score: s1,
      won: c1Won,
      lost: c2Won
    };
    $[46] = c1;
    $[47] = c1Won;
    $[48] = c2Won;
    $[49] = event.competitorOne.id;
    $[50] = nameOne;
    $[51] = s1;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== c1Won || $[54] !== c2 || $[55] !== c2Won || $[56] !== event.competitorTwo.id || $[57] !== nameTwo || $[58] !== s2) {
    t16 = {
      competitor: c2,
      id: event.competitorTwo.id,
      name: nameTwo,
      score: s2,
      won: c2Won,
      lost: c1Won
    };
    $[53] = c1Won;
    $[54] = c2;
    $[55] = c2Won;
    $[56] = event.competitorTwo.id;
    $[57] = nameTwo;
    $[58] = s2;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t15 || $[61] !== t16) {
    t17 = [t15, t16];
    $[60] = t15;
    $[61] = t16;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== isFinished || $[64] !== isLive || $[65] !== showScore || $[66] !== sport || $[67] !== t17) {
    t18 = <div className="flex flex-col gap-1">{t17.map(t19 => {
        const {
          competitor,
          id,
          name,
          score,
          won,
          lost
        } = t19;
        return <div key={id} className="flex items-center gap-1.5"><TeamLogo src={competitor?.assets?.logo} alt={name} size="sm" kind={sport.toLowerCase() === "tennis" ? "player" : "team"} countryCode={competitor?.country?.code} countryFlag={competitor?.country?.flag} /><span className={cn("text-sm font-medium flex-1 truncate", isFinished ? lost ? "text-muted-foreground/60" : "text-foreground" : "text-foreground")}>{competitor?.shortName || name}</span>{showScore && <span className={cn("font-bold text-[13px] min-w-3 text-right tabular-nums", isLive ? "text-primary" : won ? "text-foreground" : "text-muted-foreground/60")}>{score ?? "-"}</span>}</div>;
      })}</div>;
    $[63] = isFinished;
    $[64] = isLive;
    $[65] = showScore;
    $[66] = sport;
    $[67] = t17;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== t14 || $[70] !== t18) {
    t19 = <>{t14}{t18}</>;
    $[69] = t14;
    $[70] = t18;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  const matchInfo = t19;
  let t20;
  if ($[72] !== matchHref || $[73] !== matchInfo) {
    t20 = matchHref ? <Link href={matchHref} prefetch={false} className="block p-2 pb-0 cursor-pointer hover:bg-muted/40 rounded-t-md transition-colors">{matchInfo}</Link> : <div className="p-2 pb-0">{matchInfo}</div>;
    $[72] = matchHref;
    $[73] = matchInfo;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  let t21;
  if ($[75] !== bookmaker || $[76] !== isFootball || $[77] !== oddsLoading || $[78] !== processedOdds || $[79] !== showOdds || $[80] !== tenantSettings.hasOdds) {
    t21 = showOdds ? <div className="px-2 pt-2 pb-2"><BettingOdds odds={processedOdds ?? bookmaker.odds.map(_temp2)} operator={bookmaker.operator} styles={{
        container: "p-0 w-full",
        operatorContainer: "p-0",
        oddsContainer: "p-0 gap-0.5 flex px-1.5 odds-button",
        typography: "sm",
        mode: "flash",
        flashInterval: 3000
      }} /></div> : tenantSettings.hasOdds && oddsLoading && isFootball ? <OddsSkeleton /> : <div className="pb-2" />;
    $[75] = bookmaker;
    $[76] = isFootball;
    $[77] = oddsLoading;
    $[78] = processedOdds;
    $[79] = showOdds;
    $[80] = tenantSettings.hasOdds;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  let t22;
  if ($[82] !== t20 || $[83] !== t21) {
    t22 = <div className="bg-card rounded-md w-52 shrink-0 border border-border/50">{t20}{t21}</div>;
    $[82] = t20;
    $[83] = t21;
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  return t22;
}
function _temp2(t0) {
  const {
    change: change_0,
    label: label_0,
    ...rest_0
  } = t0;
  return {
    ...rest_0,
    label: label_0
  };
}
function BottomBar(t0) {
  const $ = _c(9);
  const {
    ticker
  } = t0;
  const hasLabel = !!ticker?.label;
  const hasCta = !!ticker?.cta;
  if (!hasLabel && !hasCta) {
    return null;
  }
  let t1;
  if ($[0] !== hasLabel || $[1] !== ticker) {
    t1 = hasLabel && <span className="text-muted-foreground/60 text-[11px] font-bold tracking-wide">{ticker.label}</span>;
    $[0] = hasLabel;
    $[1] = ticker;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== hasCta || $[4] !== ticker) {
    t2 = hasCta && <a href={ticker.ctaLink || "#"} target="_blank" rel="noopener noreferrer" className="bg-primary text-primary-foreground border-none rounded-sm px-3.5 py-1.5 font-bold text-[11px] cursor-pointer tracking-wide no-underline">{ticker.cta}</a>;
    $[3] = hasCta;
    $[4] = ticker;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className="flex items-center justify-between px-3 py-2 border-t border-border/50 bg-muted/40">{t1}{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// ─── Main component ──────────────────────────────────────────────────────────

type FilterId = "all" | "live" | string;
export function PopularMatchesTicker(t0) {
  const $ = _c(64);
  const {
    popularCompetitionIds,
    ticker
  } = t0;
  const {
    groups: allGroups,
    entities,
    loading,
    oddsMap,
    oddsLoading
  } = useHomeLivescoreData();
  let t1;
  if ($[0] !== allGroups || $[1] !== popularCompetitionIds) {
    t1 = applyGroupOrdering(allGroups, "popular", popularCompetitionIds, []);
    $[0] = allGroups;
    $[1] = popularCompetitionIds;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const popularGroups = t1;
  let items;
  if ($[3] !== popularGroups) {
    items = [];
    for (const group of popularGroups) {
      for (const event of group.events) {
        items.push({
          event,
          sport: group.sport,
          competitionId: group.competitionId
        });
      }
    }
    $[3] = popularGroups;
    $[4] = items;
  } else {
    items = $[4];
  }
  const popularItems = items;
  let t2;
  if ($[5] !== popularItems) {
    t2 = popularItems.filter(_temp3);
    $[5] = popularItems;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const liveItems = t2;
  let t3;
  if ($[7] !== popularItems) {
    t3 = new Set(popularItems.filter(_temp4).map(_temp5));
    $[7] = popularItems;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const footballIds = t3;
  const [activeFilter, setActiveFilter] = useState("all");
  const matchScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  let t4;
  bb0: {
    if (activeFilter === "all") {
      t4 = popularItems;
      break bb0;
    }
    if (activeFilter === "live") {
      t4 = liveItems;
      break bb0;
    }
    let t5;
    if ($[9] !== activeFilter || $[10] !== popularItems) {
      t5 = popularItems.filter(it_2 => it_2.competitionId === activeFilter);
      $[9] = activeFilter;
      $[10] = popularItems;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = t5;
  }
  const filteredItems = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const el = matchScrollRef.current;
      if (!el) {
        return;
      }
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const checkScroll = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setTimeout(checkScroll, 50);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== filteredItems) {
    t7 = [filteredItems];
    $[14] = filteredItems;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = dir => matchScrollRef.current?.scrollBy({
      left: dir * 340,
      behavior: "smooth"
    });
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const scroll = t8;
  if (loading) {
    let t9;
    if ($[17] !== ticker) {
      t9 = <TickerSkeleton ticker={ticker} />;
      $[17] = ticker;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  if (popularItems.length === 0) {
    return null;
  }
  const t9 = activeFilter === "all";
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setActiveFilter("all");
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = <FilterTab active={t9} onClick={t10}>Today</FilterTab>;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = activeFilter === "live";
  let t13;
  let t14;
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setActiveFilter("live");
    t14 = <LiveDot />;
    t15 = <span>Live</span>;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t13 = $[22];
    t14 = $[23];
    t15 = $[24];
  }
  let t16;
  if ($[25] !== activeFilter || $[26] !== liveItems.length) {
    t16 = liveItems.length > 0 && <FilterBadge active={activeFilter === "live"}>{liveItems.length}</FilterBadge>;
    $[25] = activeFilter;
    $[26] = liveItems.length;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t12 || $[29] !== t16) {
    t17 = <FilterTab active={t12} onClick={t13}>{t14}{t15}{t16}</FilterTab>;
    $[28] = t12;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== activeFilter || $[32] !== entities || $[33] !== popularGroups) {
    let t19;
    if ($[35] !== activeFilter || $[36] !== entities) {
      t19 = group_0 => {
        const competition = asCompetition(entities, group_0.competitionId);
        const compLogo = competition?.assets?.logo;
        const compName = competition?.name ?? group_0.competitionId;
        return <FilterTab key={group_0.competitionId} active={activeFilter === group_0.competitionId} onClick={() => setActiveFilter(group_0.competitionId)}>{compLogo && <img src={compLogo.replace("/process/assets", "/process/smp-images-production/assets")} alt="" width={11} height={11} className="object-contain" />}{!compLogo && competition?.country?.flag && <span className="text-[10px]">{competition.country.flag}</span>}<span className="max-w-24 truncate">{compName}</span><FilterBadge active={activeFilter === group_0.competitionId}>{group_0.events.length}</FilterBadge></FilterTab>;
      };
      $[35] = activeFilter;
      $[36] = entities;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    t18 = popularGroups.map(t19);
    $[31] = activeFilter;
    $[32] = entities;
    $[33] = popularGroups;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[38] !== t11 || $[39] !== t17 || $[40] !== t18) {
    t19 = <div className="flex items-center overflow-x-auto [scrollbar-width:none] border-b border-border/50">{t11}{t17}{t18}</div>;
    $[38] = t11;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== canScrollLeft) {
    t20 = canScrollLeft && <button onClick={() => scroll(-1)} className="absolute left-1.5 top-1/2 -translate-y-1/2 z-2 bg-muted border-none text-foreground rounded-full w-6 h-6 cursor-pointer text-[15px] flex items-center justify-center shadow-md">‹</button>;
    $[42] = canScrollLeft;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== entities || $[45] !== filteredItems || $[46] !== footballIds || $[47] !== oddsLoading || $[48] !== oddsMap) {
    t21 = filteredItems.length === 0 ? <div className="text-muted-foreground/60 text-xs py-4 px-2">No matches for this selection.</div> : filteredItems.map(t22 => {
      const {
        event: event_0,
        sport
      } = t22;
      return <MatchCard key={event_0.id} event={event_0} entities={entities} sport={sport} oddsMap={oddsMap} oddsLoading={oddsLoading} isFootball={footballIds.has(event_0.id)} />;
    });
    $[44] = entities;
    $[45] = filteredItems;
    $[46] = footballIds;
    $[47] = oddsLoading;
    $[48] = oddsMap;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t21) {
    t22 = <div ref={matchScrollRef} onScroll={checkScroll} className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] px-3 flex-1">{t21}</div>;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== canScrollRight) {
    t23 = canScrollRight && <button onClick={() => scroll(1)} className="absolute right-1.5 top-1/2 -translate-y-1/2 z-2 bg-muted border-none text-foreground rounded-full w-6 h-6 cursor-pointer text-[15px] flex items-center justify-center shadow-md">›</button>;
    $[52] = canScrollRight;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t20 || $[55] !== t22 || $[56] !== t23) {
    t24 = <div className="relative py-2.5">{t20}{t22}{t23}</div>;
    $[54] = t20;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== ticker) {
    t25 = <BottomBar ticker={ticker} />;
    $[58] = ticker;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t19 || $[61] !== t24 || $[62] !== t25) {
    t26 = <div><div className="bg-card rounded-lg overflow-hidden border border-border">{t19}{t24}{t25}</div></div>;
    $[60] = t19;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  return t26;
}

// ─── Filter helpers ──────────────────────────────────────────────────────────
function _temp5(it_1) {
  return it_1.event.id;
}
function _temp4(it_0) {
  return it_0.sport.toLowerCase() === "football";
}
function _temp3(it) {
  return it.event.status?.type?.toUpperCase() === "LIVE";
}
function FilterTab(t0) {
  const $ = _c(6);
  const {
    active,
    onClick,
    children
  } = t0;
  const t1 = active ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground/60 hover:text-muted-foreground";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex items-center gap-1 px-2.5 py-1.5 text-[11px] whitespace-nowrap shrink-0 leading-none", "border-b-2 cursor-pointer bg-transparent", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== onClick || $[4] !== t2) {
    t3 = <button onClick={onClick} className={t2}>{children}</button>;
    $[2] = children;
    $[3] = onClick;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function FilterBadge(t0) {
  const $ = _c(5);
  const {
    active,
    children
  } = t0;
  const t1 = active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("rounded-full px-1.5 text-[9.5px] font-bold leading-tight min-w-4 text-center", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <span className={t2}>{children}</span>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}